// channels.ts — pub/sub channel management for the WS bridge.
//
// safeSubscribe / safeUnsubscribe wrap ws.send() with readyState guards.
// subscribeToCaster + subscribeToEncounterRollChannel rotate channel
// subscriptions when the active caster / encounter changes (caster swap
// in the header, or player moving between encounters).

import { ws } from './state';
import { encounterReplay } from './encounter-replay';
import { fetchAndCacheCaster, findCasterInEncounterCache } from './caster-cache';

// Subscription intent belongs to a connection, including while its ack is pending.
// The bridge still owns grants, TTL checks and authority invalidation on delivery.
const channelIntents = new WeakMap<WebSocket, Set<string>>();

// Reuse setup's parsed control; do not parse every gameplay frame a second time.
window.addEventListener('ft:ws-channel-subscribed', event => {
    const control = (event as CustomEvent<{ socket?: WebSocket; channel?: string; status?: string }>).detail;
    if (control?.socket && typeof control.channel === 'string'
        && (control.status === 'refused' || control.status === 'revoked')) {
        channelIntents.get(control.socket)?.delete(control.channel.toUpperCase());
    }
});

/**
 * Safely subscribe to a WebSocket channel.
 * - Sends each channel once per OPEN socket until unsubscribed, refused or revoked.
 * - Warns if the socket is not ready.
 * - Logs the exact payload for debugging.
 */
function safeSubscribe(channel: string, socket: WebSocket | null | undefined = ws): void {
    if (!channel || typeof channel !== 'string') {
        console.error('safeSubscribe() → Invalid channel:', channel);
        return;
    }

    if (socket && socket.readyState === WebSocket.OPEN) {
        let intents = channelIntents.get(socket);
        if (!intents) {
            intents = new Set<string>();
            channelIntents.set(socket, intents);
        }
        const key = channel.toUpperCase();
        if (intents.has(key)) return;
        const payload = JSON.stringify({ type: 'subscribe', channel, ...encounterReplay.subscription(channel) });
        socket.send(payload);
        intents.add(key);
        ftDebugLog('ws', { subscribed: channel, payload });
    } else {
        ftDebugLog('ws', { warn: 'WebSocket not ready', readyState: socket?.readyState ?? 'null', channel });
    }
}

function safeUnsubscribe(ch: string): void {
    if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'unsubscribe', channel: ch }));
        channelIntents.get(ws)?.delete(ch.toUpperCase());
    }
}

/**
 * (Re)subscribe to the current caster channel.
 * Call it:
 *   • on page load (once the initial caster_id is known)
 *   • every time the GM clicks a creature → caster_id changes
 */
async function subscribeToCaster(): Promise<void> {
    const sl = getSidelogContext();
    // Caster-channel bookkeeping lives on window.FateTide (not the #caster_id element's dataset) so it
    // survives the hidden-input deprecation. [cache-sourced caster 2026-06-22]
    const ftState = window.FateTide ??= {};

    if (!sl?.caster_id || !checkIfValidUUID(sl.caster_id)) {
        // No active caster → drop any live caster-channel sub before clearing state.
        if (ftState.casterChannelSub) {
            safeUnsubscribe(`caster:${ftState.casterChannelSub}`);
            ftState.casterChannelSub = null;
        }
        if (window.FateTide) {
            window.FateTide.activeViewerCasterId = null;
            // Drop ONLY the previously-active caster's entry — NOT casterCache.clear(). The
            // casterCache is ALSO the page's static blob store (item.php's static-creature
            // route-mount seed, monster-template preview seed), and a viewer with no active PC
            // hits this branch at WS-open on EVERY page load — the full clear() wiped those
            // page seeds moments after they landed, so a side-info click on a cache-only node
            // (a static creature's synthetic enchanted equipped gear) fell through to the
            // server and 404'd (Nasten regalia bug, 2026-07-13). Active-caster state is fully
            // reset by the unsubscribe above + deleting its entry + nulling the ids; unrelated
            // page seeds must survive.
            if (ftState.prevCasterId) window.FateTide.casterCache?.delete(ftState.prevCasterId);
        }
        ftState.prevCasterId = null;
        return;
    }

    const newId = sl.caster_id;
    const old = ftState.prevCasterId;

    // Rotate OFF the previous active caster's channel (only if it actually had one — an
    // encounter-managed caster never subscribed) + drop its casterCache alias.
    if (old && old !== newId) {
        if (ftState.casterChannelSub === old) safeUnsubscribe(`caster:${old}`);
        window.FateTide?.casterCache?.delete(old);
    }

    // ENCOUNTER-MANAGED GATE (unified-viewer-scoping §4.1 chunk 1): when the active caster is a
    // creature inside a cached FULL encounter blob, the encounter_update_delta wire
    // (encounter-delta.ts) already carries its every section change AND runs the full
    // applyCasterSideEffects for the viewer's own active caster — so the per-caster `caster:{id}`
    // channel is pure double-delivery (§4.6). Skip it (and cut it if a prior call / the WS-open
    // race opened it). A caster NOT in the loaded encounter (out-of-encounter summon scouting
    // another room, standalone sheet) keeps its channel — the gate self-handles that case. The
    // page-load race (cache not yet seeded at WS open) is closed by
    // reconcileCasterChannelForEncounterLoad(), invoked once the full blob lands.
    const encounterManaged = !!findCasterInEncounterCache(newId);
    if (encounterManaged) {
        if (ftState.casterChannelSub === newId) safeUnsubscribe(`caster:${newId}`);
        ftState.casterChannelSub = null;
    } else {
        safeSubscribe(`caster:${newId}`);
        ftState.casterChannelSub = newId;
    }

    // THIS IS THE KEY: Force-load the caster data if not cached (resolves instantly from the
    // encounter blob via seedCasterFromEncounterCache when the creature is in the loaded encounter).
    if (!window.FateTide?.casterCache?.has(newId)) {
        ftDebugLog('caster', { cacheMissFetch: newId });
        await fetchAndCacheCaster(newId);
    }

    if (window.FateTide) window.FateTide.activeViewerCasterId = newId;

    ftState.prevCasterId = newId;

    ftDebugLog('caster', { viewerActive: newId, encounterManaged });


}

/**
 * (Re)subscribe to the encounter-scoped player roll channel.
 * Mirrors subscribeToCaster()'s shape: tracks the previous encounter on the
 * window.FateTide.prevRollEncounter and rotates the subscription on active encounter change.
 *
 * - GMs are no-op here: their roll channel is roll:{campaign_id}:gm and stays
 *   bound at setupWebSocketHandlers() time.
 * - Players move encounter A → B by unsubscribing roll:A:player and subscribing
 *   roll:B:player. Without the rotation the bridge would keep delivering A's
 *   rolls until the WS disconnect.
 */
function subscribeToEncounterRollChannel(): void {
    if (window.FateTide?.user?.isGM) return;

    const ftState = window.FateTide ??= {};
    const newId = String(window.ftGetActiveEncounterId?.() || '').trim();
    const prev = String(ftState.prevRollEncounter || '').trim();

    if (prev && prev !== newId && checkIfValidUUID(prev)) {
        safeUnsubscribe(`roll:${prev}:player`);
    }

    if (checkIfValidUUID(newId)) {
        safeSubscribe(`roll:${newId}:player`);
        ftState.prevRollEncounter = newId;
    } else {
        delete ftState.prevRollEncounter;
    }
}

/**
 * Close the WS-open vs encounter-load race for the encounter-managed gate (unified-viewer-scoping
 * §4.1 chunk 1). subscribeToCaster() may run at WS `onopen` BEFORE the full encounter blob has been
 * seeded into encounterCache; in that window
 * findCasterInEncounterCache() returns null and the active caster's `caster:{id}` channel is
 * subscribed as the safe fallback. Once the full blob lands (template.js XZ widget init, after
 * ftRealiasCastersFromEncounterBlob), call this: if the still-subscribed active caster is now
 * resolvable inside a loaded encounter, the encounter channel carries its deltas, so cut the
 * redundant caster channel. Cheap and idempotent — no fetch / no visibility re-churn.
 */
function reconcileCasterChannelForEncounterLoad(): void {
    const ftState = window.FateTide ??= {};
    const sub = ftState.casterChannelSub;
    if (!sub) return;
    if (findCasterInEncounterCache(sub)) {
        safeUnsubscribe(`caster:${sub}`);
        ftState.casterChannelSub = null;
        ftDebugLog('caster', { encounterManagedCutAfterLoad: sub });
    }
}

/**
 * Close the WS-open vs encounter-load race for the encounter channel itself. setupWebSocketHandlers()
 * computes `encounter:{id}` once, at `onopen`, from ftGetActiveEncounterId(). The encounter surface's
 * scene frame (tools/encounters/ft3d_scene_host.php) starts its socket at template.js load, before its
 * live encounter read installs the blob that makes that resolver answer, so whenever the handshake won
 * the race the frame never subscribed its encounter: every saved-row delivery for it (a status retired by
 * another encounter's deletion, any base_patch) missed the actor's AND each observer's held cache
 * (statustails34, three served failures 2026-11-03). Subscribe the active encounter as soon as it is
 * known: on an explicit set (ft:active-encounter-change) and when a blob lands (ft:encounter-installed,
 * the moment the resolver's cache fallbacks become true). Subscribe only: safeSubscribe sends once per
 * OPEN socket, a socket still connecting subscribes at its own onopen, and a reconnect recomputes there.
 * Channel rotation stays with its owners (the campaign page's fallback, the player roll rotation above).
 */
function subscribeToActiveEncounter(): void {
    const encounterId = String(window.ftGetActiveEncounterId?.() || '').trim();
    if (checkIfValidUUID(encounterId)) safeSubscribe(`encounter:${encounterId}`);
}
for (const type of ['ft:active-encounter-change', 'ft:encounter-installed']) window.addEventListener(type, subscribeToActiveEncounter);

window.safeSubscribe = safeSubscribe;
window.safeUnsubscribe = safeUnsubscribe;
window.subscribeToCaster = subscribeToCaster;
window.subscribeToEncounterRollChannel = subscribeToEncounterRollChannel;
window.reconcileCasterChannelForEncounterLoad = reconcileCasterChannelForEncounterLoad;

export { safeSubscribe, safeUnsubscribe, subscribeToCaster, subscribeToEncounterRollChannel, reconcileCasterChannelForEncounterLoad, subscribeToActiveEncounter };
